"use client";

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../lib/utils";

const avatarVariants = cva(
  "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
  {
    variants: {
      size: {
        default: "h-10 w-10",
        sm: "h-8 w-8",
        lg: "h-12 w-12",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> &
    VariantProps<typeof avatarVariants> & {
      asChild?: boolean;
    } & React.HTMLAttributes<HTMLSpanElement>
>(({ className, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : AvatarPrimitive.Root;
  return (
    <Comp
      ref={ref}
      className={cn(avatarVariants({ size, className }))}
      {...props}
    />
  );
});
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> & {
    asChild?: boolean;
  } & React.HTMLAttributes<HTMLImageElement>
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : AvatarPrimitive.Image;
  return (
    <Comp
      ref={ref}
      className={cn("aspect-square h-full w-full", className)}
      {...props}
    />
  );
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & {
    asChild?: boolean;
  } & React.HTMLAttributes<HTMLSpanElement>
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : AvatarPrimitive.Fallback;
  return (
    <Comp
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full bg-muted",
        className,
      )}
      {...props}
    />
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
